import Icon from "@ant-design/icons/lib/components/Icon";
import { Button, Col, Modal, Row, Typography } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useIsMutating, useQuery } from "react-query";
import { fetchBookableDate } from "../../../../../apis/This";
import useModals from "../../../../../hooks/useModals";
import useUser from "../../../../../hooks/useUsers";
import { HttpError } from "../../../../../network/types";
import { alertError } from "../../../../../shared/utils";
import { Close } from "../../../Icon";
import { BooksCalendar } from "../../../Unbookable/Calendar";
import { CustomCard } from "../../Detail/Card.styled";
import { BooksTimes } from "./Time";

interface ReservationOnsiteDateTimeChangeModalProps {
  defaultValue?: string;
  onClose?: (dateTime: moment.Moment | undefined) => void;
}

export const ReservationOnsiteDateTimeChangeModal: React.FC<
  ReservationOnsiteDateTimeChangeModalProps
> = ({ defaultValue, onClose = () => void 0 }) => {
  const { getUserID } = useUser();
  const { closeModal, openConfirmModal } = useModals();
  const isMutating = useIsMutating();

  const [selected, setSelected] = useState<moment.Moment | undefined>();
  const [selectTime, setSelectTime] = useState<moment.Moment | undefined>();

  /**
   * 변경가능 일자 조회하기
   */
  const { data, isError, error } = useQuery(
    ["BookableDates", getUserID()],
    fetchBookableDate,
    {
      refetchOnMount: "always",
      onSuccess: (data) => {
        /**
         * 날짜가 있으면 해당 날짜로 선택
         */
        if (data.bookableDate?.length) {
          const started = moment(data.bookableDate[0]).startOf("date");
          /**
           * 기존 선택날짜가 있고, 해당날짜가 가능날짜 내면 기존데이터 유지
           */
          if (defaultValue) {
            const isBetween = moment(defaultValue).isBetween(
              started,
              moment(data.bookableDate[data.bookableDate.length - 1]).endOf(
                "date"
              ),
              undefined,
              "[]"
            );

            if (isBetween) {
              setSelected(moment(defaultValue));
            } else {
              setSelected(started);
            }
          } else {
            setSelected(started);
          }
        }
      },
      onError: (err: HttpError) => {
        if (err?.status !== 500) {
          alertError(err.message);
        }
      },
    }
  );

  const handleOk = () => {
    /**
     * 변경요청하기
     */
    if (selectTime) {
      openConfirmModal({
        title: "출장 정보를 변경하시겠어요?",
        contents: "정보 변경 시 알림톡이 발송됩니다.",
        onOk: () => {
          closeModal();
          onClose(selectTime);
        },
      });
    }
  };

  useEffect(() => {
    if (defaultValue && selected?.isSame(moment(defaultValue), "date")) {
      setSelectTime(moment(defaultValue));
    } else {
      setSelectTime(undefined);
    }
  }, [selected]);

  useEffect(() => {
    setSelected(moment(defaultValue));
  }, [defaultValue]);

  return (
    <Modal
      open
      title="일시 변경"
      closeIcon={<Icon component={Close} />}
      onOk={() => handleOk}
      onCancel={closeModal}
      centered
      width={700}
      bodyStyle={{ padding: 0 }}
      footer={
        <Row justify="space-between" align="middle">
          <Col>
            <Typography.Text strong>
              변경전 일시: {moment(defaultValue)?.format("YYYY.MM.DD HH:mm")}
            </Typography.Text>
          </Col>
          <Col>
            <Row gutter={16}>
              <Col>
                <Button size="large" onClick={closeModal}>
                  취소
                </Button>
                <Button
                  size="large"
                  type="primary"
                  onClick={handleOk}
                  disabled={!selectTime}
                  loading={!!isMutating}
                >
                  저장
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      }
    >
      <CustomCard
        style={{
          height: "100%",
          display: "inline-flex",
          overflow: "hidden",
        }}
        bordered={false}
      >
        <CustomCard.Grid
          style={{ width: "50%", height: "56px", padding: "16px 24px" }}
          hoverable={false}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Typography.Title level={5} style={{ margin: 0 }}>
                날짜 선택
              </Typography.Title>
            </Col>
            <Col>
              <Typography.Title level={5} style={{ margin: 0 }}>
                {selected?.format("YYYY.MM.DD")}
              </Typography.Title>
            </Col>
          </Row>
        </CustomCard.Grid>
        <CustomCard.Grid
          style={{ width: "50%", height: "56px", padding: "16px 24px" }}
          hoverable={false}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Typography.Title level={5} style={{ margin: 0 }}>
                시간 선택
              </Typography.Title>
            </Col>
            <Col>
              <Typography.Title level={5} style={{ margin: 0 }}>
                {selectTime?.format("HH:mm")}
              </Typography.Title>
            </Col>
          </Row>
        </CustomCard.Grid>
        <CustomCard.Grid
          style={{
            width: "50%",
            height: "calc(100% - 56px)",
            overflow: "auto",
          }}
          hoverable={false}
        >
          <BooksCalendar
            current={selected}
            disableDates={data?.bookableDate.map((date) => moment(date))}
            onChange={(date) => setSelected(date)}
          />
        </CustomCard.Grid>
        <CustomCard.Grid
          style={{
            width: "50%",
            height: "calc(100% - 56px)",
            overflow: "auto",
          }}
          hoverable={false}
        >
          {selected && (
            <BooksTimes
              date={selected}
              time={selectTime}
              onChange={(time) => setSelectTime(time)}
            />
          )}
        </CustomCard.Grid>
      </CustomCard>
    </Modal>
  );
};
